import unescapedText from 'I18n/utils/unescapedText';
import { View } from 'foundations-components/components/View';
import { Form } from 'foundations-components/transitional/components/Form';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useDebounceValue } from 'marketplace-ui-common/hooks/useDebounceValue';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchDropdown } from './SearchDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function SearchBar({
  inputValue,
  isLoading = false,
  minSearchLength = 2,
  onClear,
  onInputValueChange,
  onOpenChange,
  onResultClick,
  onSearchSubmit,
  options,
  readOnly = false,
  use = 'default'
}) {
  const [searchTerm, setSearchTerm] = useState(inputValue !== null && inputValue !== void 0 ? inputValue : '');
  const debouncedSearchTerm = useDebounceValue(searchTerm);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef(null);

  // Memoize handlers to prevent unnecessary re-renders
  const handleOutsideClick = useCallback(e => {
    var _containerRef$current;
    if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(e.target)) return;
    setShowSuggestions(false);
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
  }, [onOpenChange]);
  const closeDropdown = useCallback(() => {
    setShowSuggestions(false);
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
  }, [onOpenChange]);

  // Effect to handle input value changes
  useEffect(() => {
    if (debouncedSearchTerm === inputValue) return;

    // Only trigger input value change if meets minimum length or is empty
    if (debouncedSearchTerm.length >= minSearchLength || debouncedSearchTerm === '') {
      onInputValueChange === null || onInputValueChange === void 0 || onInputValueChange({
        target: {
          value: debouncedSearchTerm
        }
      });
    }
  }, [debouncedSearchTerm, inputValue, onInputValueChange, minSearchLength]);

  // Handle outside clicks
  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [handleOutsideClick]);
  const handleSubmit = useCallback(e => {
    var _searchTerm$trim;
    e.preventDefault();
    const trimmedTerm = (_searchTerm$trim = searchTerm === null || searchTerm === void 0 ? void 0 : searchTerm.trim()) !== null && _searchTerm$trim !== void 0 ? _searchTerm$trim : '';
    if (!trimmedTerm || trimmedTerm.length < minSearchLength) return;
    onSearchSubmit === null || onSearchSubmit === void 0 || onSearchSubmit(trimmedTerm);
    closeDropdown();
  }, [searchTerm, onSearchSubmit, closeDropdown, minSearchLength]);
  const handleInputChange = useCallback(e => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (!newValue.trim() && onClear) {
      onClear();
    }
  }, [onClear]);
  const handleFocus = useCallback(() => {
    setShowSuggestions(true);
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(true);
  }, [onOpenChange]);
  const handleResultSelect = useCallback(result => {
    if (typeof result === 'string') {
      setSearchTerm(result);
      onResultClick === null || onResultClick === void 0 || onResultClick(result);
      closeDropdown();
    }
  }, [onResultClick, closeDropdown]);
  return /*#__PURE__*/_jsx("div", {
    ref: containerRef,
    children: /*#__PURE__*/_jsxs(View, {
      blockSize: "40px",
      maxInlineSize: "600px",
      position: "relative",
      children: [/*#__PURE__*/_jsx(Form, {
        onSubmit: handleSubmit,
        children: /*#__PURE__*/_jsx(SearchInput, {
          "data-test-id": "storefront-search-input",
          placeholder: unescapedText('marketplaceStorefront.searchBar.placeholder'),
          value: searchTerm,
          onChange: handleInputChange,
          onFocus: handleFocus,
          disabled: readOnly,
          use: use
        })
      }), /*#__PURE__*/_jsx(SearchDropdown, {
        isLoading: isLoading,
        onSelectResult: handleResultSelect,
        options: options,
        showSuggestions: showSuggestions,
        use: use
      })]
    })
  });
}